// src/features/issuers/new.tsx
import { Form } from "@/components/ui/form";
import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  AutocompleteAsyncField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";
import { NewDiff } from "@/features/FormCard/NewDiff";
import {
  generateDefaultValues,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import i18n from "@/i18n";
import {
  amfMonetaryTypeOptions,
  expositionIssuerRatingOptions,
  expositionIssuerTypeOptions,
  issuerTypeOptions,
  oldSectorOptions,
  sectorOptions,
  typeOfIdentificationCodeForIssuerGroupOptions,
} from "./filter";
import { fieldTranslations } from "./translations";

const client = "instit-quantilia";
const table_name = "issuers";

// Zod schema for the form
const FormSchema = z.object({
  name: z.string().min(1, "Name is required"),

  // Enums (use string keys as per AR enum)
  amf_monetary_type: z.string().nullable().optional(),
  sector: z.string().nullable().optional(),
  old_sector: z.string().nullable().optional(),
  issuer_type: z.string().nullable().optional(),
  exposition_issuer_type: z.string().nullable().optional(),
  exposition_issuer_rating: z.string().nullable().optional(),
  type_of_identification_code_for_issuer_group: z
    .string()
    .optional()
    .nullable(),

  // Country selects (ID fields) - send as strings
  country_id: z.coerce.number().nullable().optional(),
  country: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string().optional().nullable(),
      // for the autocomplete, the form state is an object
      z.object({
        id: z.number(),
        text: z.string(),
      }),
    ]),
  ),
  fund_registration_country_id: z.coerce.number().nullable().optional(),
  fund_registration_country: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string().optional().nullable(),
      // for the autocomplete, the form state is an object
      z.object({
        id: z.number(),
        text: z.string(),
      }),
    ]),
  ),

  // Grouped select for industry - send as string
  industry: z.string().nullable().optional(),

  // Other text fields
  source: z.string().nullable().optional(),
  lei: z
    .string()
    .refine(
      (val) => val === "" || /^[A-Za-z0-9]{20}$/.test(val),
      i18n.t("validation.lei_length"),
    )
    .nullable()
    .optional(),
  vigeo_key: z.string().nullable().optional(),
  vigeo_title: z.string().nullable().optional(),
  name_of_the_group_of_the_issuer: z.string().nullable().optional(),
  identification_of_the_group: z.string().nullable().optional(),
  economic_sector: z.string().nullable().optional(),
  sustainalytics_entity_name: z.string().nullable().optional(),
  msci_entity_id: z.string().nullable().optional(),
  msci_entity_name: z.string().nullable().optional(),

  // AutocompleteAsync ID fields - receive numbers from component
  direct_parent_id: z.coerce.number().optional().nullable(),
  ultimate_parent_id: z.coerce.number().optional().nullable(),
  market_cap_amount: z.coerce.number().optional().nullable(),
  market_cap_currency: z.string().nullable().optional(),
  market_cap_amount_in_usd: z.coerce.number().optional().nullable(),
  sustainalytics_entity_id: z.coerce.number().optional().nullable(),

  // Autocomplete async fields: display + ID
  direct_parent_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({ id: z.number(), name: z.string() }),
      ])
      .optional(),
  ),
  ultimate_parent_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({ id: z.number(), name: z.string() }),
      ])
      .optional(),
  ),
});

export function IssuersNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const currencyOptions = useCurrencyOptions();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    translations,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
  });

  const DEFAULTS = generateDefaultValues(FormSchema, {});

  const onResetNew = () => {
    form.reset(DEFAULTS, {
      keepDirty: false,
      keepTouched: false,
      keepErrors: false,
      keepValues: false,
    });
    handleReset();
  };

  const navigateToList = () => navigate(-1);

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New Issuer
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="name"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Name",
                    })}
                    isRequired
                    control={form.control}
                    capitalize={true}
                  />
                  <InputField
                    name="lei"
                    label={t(`${table_name}.columns.lei`, {
                      ns: "tables",
                      defaultValue: "LEI",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="country"
                    label={t(`${table_name}.columns.country`, {
                      ns: "tables",
                      defaultValue: "Country",
                    })}
                    id_column="country_id"
                    control={form.control}
                    base_url="instit-quantilia/countries"
                    column_name="en_translation"
                    showId={false}
                  />
                  <SelectField
                    name="amf_monetary_type"
                    label={t(`${table_name}.columns.en_translation`, {
                      ns: "tables",
                      defaultValue: "AMF Monetary Type",
                    })}
                    options={amfMonetaryTypeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="issuer_type"
                    label={t(`${table_name}.columns.issuer_type`, {
                      ns: "tables",
                      defaultValue: "Issuer Type",
                    })}
                    options={issuerTypeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="industry"
                    label={t(`${table_name}.columns.industry`, {
                      ns: "tables",
                      defaultValue: "Industry",
                    })}
                    control={form.control}
                    options={translations?.["industry"]}
                    allow_empty_option
                  />
                </div>

                {/* Row 4 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="sector"
                    label={t(`${table_name}.columns.sector`, {
                      ns: "tables",
                      defaultValue: "Sector",
                    })}
                    options={sectorOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="old_sector"
                    label={t(`${table_name}.columns.old_sector`, {
                      ns: "tables",
                      defaultValue: "Old Sector",
                    })}
                    options={oldSectorOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                {/* Row 5 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="direct_parent_name"
                    label={t(`${table_name}.columns.direct_parent_name`, {
                      ns: "tables",
                      defaultValue: "Direct Parent",
                    })}
                    base_url="instit-quantilia/issuers"
                    column_name="name"
                    id_column="direct_parent_id"
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="ultimate_parent_name"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Ultimate Parent",
                    })}
                    base_url="instit-quantilia/issuers"
                    column_name="name"
                    id_column="ultimate_parent_id"
                    control={form.control}
                  />
                </div>

                {/* Row 7 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="market_cap_amount"
                    label={t(`${table_name}.columns.market_cap_amount`, {
                      ns: "tables",
                      defaultValue: "Market Cap Amount",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <SelectField
                    name="market_cap_currency"
                    label={t(`${table_name}.columns.market_cap_currency`, {
                      ns: "tables",
                      defaultValue: "Market Cap Currency",
                    })}
                    options={currencyOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                {/* Row 6 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="market_cap_amount_in_usd"
                    label={t(`${table_name}.columns.market_cap_amount_in_usd`, {
                      ns: "tables",
                      defaultValue: "Market Cap Amount (USD)",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="source"
                    label={t(`${table_name}.columns.source`, {
                      ns: "tables",
                      defaultValue: "Source",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 8 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="exposition_issuer_type"
                    label={t(`${table_name}.columns.exposition_issuer_type`, {
                      ns: "tables",
                      defaultValue: "Exposition Issuer Type",
                    })}
                    options={expositionIssuerTypeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="exposition_issuer_rating"
                    label={t(`${table_name}.columns.exposition_issuer_rating`, {
                      ns: "tables",
                      defaultValue: "Exposition Issuer Rating",
                    })}
                    options={expositionIssuerRatingOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                {/* Row 9 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="vigeo_key"
                    label={t(`${table_name}.columns.vigeo_key`, {
                      ns: "tables",
                      defaultValue: "Vigeo Key",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="vigeo_title"
                    label={t(`${table_name}.columns.vigeo_title`, {
                      ns: "tables",
                      defaultValue: "Vigeo Title",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 10 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="name_of_the_group_of_the_issuer"
                    label={t(
                      `${table_name}.columns.name_of_the_group_of_the_issuer`,
                      { ns: "tables", defaultValue: "Group Name" },
                    )}
                    control={form.control}
                  />
                  <InputField
                    name="identification_of_the_group"
                    label={t(
                      `${table_name}.columns.identification_of_the_group`,
                      {
                        ns: "tables",
                        defaultValue: "Identification of the Group",
                      },
                    )}
                    control={form.control}
                  />
                </div>

                {/* Row 11 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="type_of_identification_code_for_issuer_group"
                    label={t(
                      `${table_name}.columns.type_of_identification_code_for_issuer_group`,
                      {
                        ns: "tables",
                        defaultValue: "Identification Code Type",
                      },
                    )}
                    options={typeOfIdentificationCodeForIssuerGroupOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <InputField
                    name="economic_sector"
                    label={t(`${table_name}.columns.economic_sector`, {
                      ns: "tables",
                      defaultValue: "Economic Sector",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 12 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="sustainalytics_entity_id"
                    label={t(`${table_name}.columns.sustainalytics_entity_id`, {
                      ns: "tables",
                      defaultValue: "Sustainalytics Entity ID",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="sustainalytics_entity_name"
                    label={t(
                      `${table_name}.columns.sustainalytics_entity_name`,
                      {
                        ns: "tables",
                        defaultValue: "Sustainalytics Entity Name",
                      },
                    )}
                    control={form.control}
                  />
                </div>

                {/* Row 13 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="msci_entity_id"
                    label={t(`${table_name}.columns.msci_entity_id`, {
                      ns: "tables",
                      defaultValue: "MSCI Entity ID",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="msci_entity_name"
                    label={t(`${table_name}.columns.msci_entity_name`, {
                      ns: "tables",
                      defaultValue: "MSCI Entity Name",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 14 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="fund_registration_country"
                    label={t(
                      `${table_name}.columns.fund_registration_country`,
                      {
                        ns: "tables",
                        defaultValue: "Fund Registration Country",
                      },
                    )}
                    id_column="fund_registration_country_id"
                    control={form.control}
                    base_url="instit-quantilia/countries"
                    column_name="en_translation"
                    showId={false}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={onResetNew}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields
              ? NewDiff(
                  changedFields,
                  [
                    "country_id",
                    "direct_parent_id",
                    "ultimate_parent_id",
                    "fund_registration_country_id",
                  ],
                  fieldTranslations,
                )
              : "No changes detected"}
          </ConfirmationModal>

          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
